import React, { useState, useEffect, useCallback } from 'react';
import { Trophy, Medal, Award } from 'lucide-react';
import Header from '../components/Header';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { participationsAPI, handleApiError } from '../utils/api';

const formatEuros = (value) =>
  new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(
    value || 0
  );

const RANK_ICONS = [
  { Icon: Trophy, className: 'text-yellow-500' },
  { Icon: Medal, className: 'text-gray-400' },
  { Icon: Award, className: 'text-amber-700' },
];

const LeaderboardPage = () => {
  const { userRole } = useAuth();
  const navigate = useNavigate();
  const [leaderboard, setLeaderboard] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadLeaderboard = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await participationsAPI.getLeaderboard();
      setLeaderboard(response.data || []);
    } catch (err) {
      const { error: errTitle, details } = handleApiError(err);
      setError(`${errTitle} : ${details}`);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (userRole === 'admin') loadLeaderboard();
  }, [loadLeaderboard, userRole]);

  if (userRole !== 'admin') {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="container mx-auto px-4 py-8 text-center">
          <h2 className="text-2xl font-bold text-red-600 mb-4">Accès refusé</h2>
          <p className="text-gray-600 mb-6">Vous devez être administrateur pour accéder à cette page.</p>
          <button
            onClick={() => navigate('/')}
            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition"
          >
            Retour à l'accueil
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-lg shadow-lg overflow-hidden mb-6">
            <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-6">
              <h1 className="text-3xl font-bold flex items-center">
                <Trophy className="mr-3" size={32} />
                Classement Général
              </h1>
              <p className="mt-2 text-blue-100">
                Basé sur les résultats saisis par les organisateurs pour chaque tournoi
              </p>
            </div>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-4 mb-6">
              {error}
            </div>
          )}

          {isLoading ? (
            <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">
              Chargement du classement...
            </div>
          ) : leaderboard.length === 0 ? (
            <div className="bg-white rounded-lg shadow p-8 text-center">
              <p className="text-gray-500">
                Aucun résultat n'a encore été saisi. Le classement apparaîtra dès que des résultats
                de tournois seront enregistrés.
              </p>
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow-lg overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-100 text-left text-sm text-gray-600">
                  <tr>
                    <th className="px-4 py-3">#</th>
                    <th className="px-4 py-3">Joueur</th>
                    <th className="px-4 py-3 text-right">Tournois joués</th>
                    <th className="px-4 py-3 text-right">Meilleure place</th>
                    <th className="px-4 py-3 text-right">Gains cumulés</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {leaderboard.map((entry, index) => {
                    const rankIcon = RANK_ICONS[index];
                    return (
                      <tr key={entry.userId} className={index < 3 ? 'bg-yellow-50/40' : 'hover:bg-gray-50'}>
                        <td className="px-4 py-3 font-bold text-gray-700">
                          <div className="flex items-center gap-2">
                            {rankIcon ? <rankIcon.Icon className={rankIcon.className} size={20} /> : null}
                            {index + 1}
                          </div>
                        </td>
                        <td className="px-4 py-3 font-semibold text-gray-800">{entry.username}</td>
                        <td className="px-4 py-3 text-right text-gray-600">{entry.tournamentsPlayed}</td>
                        <td className="px-4 py-3 text-right text-gray-600">
                          {entry.bestFinish ? `${entry.bestFinish}${entry.bestFinish === 1 ? 'er' : 'e'}` : '—'}
                        </td>
                        <td className="px-4 py-3 text-right font-bold text-green-600">
                          {formatEuros(entry.totalPrize)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LeaderboardPage;
